// Allotment Letter pop-up (M8-F6; rows M8.4 / M8.14 / M8.21 / M8.22 / M8.24; DC-143 / DC-168), prototype letterModal.
// Read only: the letter is made by the server at Final Review approval (M12); there is no edit, regenerate or "cancel and
// reissue" here (Reissue is M12's own pop-up). Shows the chip (Waiting for eSign / eSign refused / eSigned), the issue
// line, the applicants named on the letter, who it was sent to, the eSign line and the lock line. Download opens a
// 10-minute link to the letter (or the eSigned copy once signed).
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import { crm2AllotmentLetter, crm2AllotmentLetterLink } from "../../../Services/Crm2List";
import { errorTost } from "../../../Services/Toast";
import { Chip } from "../ui/cardParts";
import settingsCss from "../Settings/Crm2Settings.module.css";

const when = (ms) => (ms ? new Date(Number(ms)).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const btnSec = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem" };
const btnPri = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#155EEF", border: "1px solid #155EEF", fontSize: "1.4rem", lineHeight: "2rem" };
const grid = { display: "grid", gridTemplateColumns: "auto 1fr", gap: ".4rem 1.6rem", fontSize: "1.4rem" };

export default function AllotmentLetterModal({ card, onClose }) {
  const [s, setS] = useState(null);
  const [failed, setFailed] = useState("");

  useEffect(() => {
    crm2AllotmentLetter(card.allotmentPaymentId)
      .then(setS)
      .catch((e) => {
        console.error("[crm2][AllotmentLetterModal] load failed", e);
        setFailed(e?.response?.data?.message || e.message);
      });
  }, [card.allotmentPaymentId]);

  const download = async () => {
    try {
      const { url } = await crm2AllotmentLetterLink(card.allotmentPaymentId, s?.status === "SIGNED" ? "signed" : "letter");
      window.open(url, "_blank", "noopener");
    } catch (e) {
      errorTost(e?.response?.data?.message || e.message);
    }
  };

  const signed = s?.status === "SIGNED";
  const refused = s?.status === "REFUSED";
  const last = s?.lastAttempt;
  return (
    <Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={settingsCss.modalWrapper} style={{ width: "min(76rem, 94vw)", maxWidth: "94vw" }}>
        <div className={settingsCss.modalHeader}>
          <span>Allotment Letter</span>
          <span className={settingsCss.modalClose} onClick={onClose} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={settingsCss.modalContent} style={{ maxHeight: "72vh", overflow: "auto", display: "grid", gap: "1.4rem", fontSize: "1.4rem" }}>
          {!s && !failed && <p style={{ color: "#667085" }}>Loading…</p>}
          {failed && <p style={{ color: "#B42318" }}>{failed}</p>}
          {s && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
                <b style={{ color: "#101828", fontSize: "1.6rem" }}>
                  {s.unitLabel} | {s.projectName}
                </b>
                {signed ? <Chip text="eSigned" tone="c-ok" /> : refused ? <Chip text="eSign refused: sign again" tone="c-err" /> : s.letter.state === "MADE" ? <Chip text="Waiting for eSign" tone="c-warn" /> : <Chip text="Letter not made" tone="c-err" />}
              </div>
              <div style={{ border: "1px solid #EAECF0", borderRadius: ".8rem", padding: "1.6rem", background: "#FCFCFD", display: "grid", gap: ".8rem" }}>
                {s.letter.state === "MADE" ? (
                  <p style={{ color: "#475467", margin: 0 }}>
                    Issued {when(s.letter.issuedAt)}
                    {s.letter.allotmentNo ? ` · Allotment No. ${s.letter.allotmentNo}` : ""} · built on the server from the project's allotment letter template · {s.letter.signedByVj}
                  </p>
                ) : (
                  <p style={{ color: "#B42318", margin: 0 }}>
                    Allotment letter not made{s.letter.reason ? `: ${s.letter.reason}` : ""}
                    {s.letter.state === "RETRYING" ? " · trying again" : ""}
                  </p>
                )}
                <p style={{ margin: 0 }}>
                  <b>Applicants (all named on the letter)</b>
                </p>
                {(s.applicants || []).map((a) => (
                  <p key={a.applicantId} style={{ margin: 0 }}>
                    {a.no}. {a.name}
                  </p>
                ))}
              </div>
              <div style={grid}>
                <span style={{ color: "#475467" }}>Sent to</span>
                <span>
                  Every applicant · email and WhatsApp · Customer Portal
                  {s.sends?.length ? ` · last ${s.sends[0].kind.toLowerCase()} ${when(s.sends[0].at)}` : ""}
                </span>
                <span style={{ color: "#475467" }}>eSign</span>
                <span>
                  {signed
                    ? `eSigned · ${s.signed.name || "—"}${s.signed.last4 ? ` · Aadhaar XXXX ${s.signed.last4}` : ""} · ${when(s.signed.at)}${s.signed.txn ? ` · Surepass txn ${s.signed.txn}` : ""} · name matched`
                    : refused
                    ? `Refused: ${last?.aadhaarName ? `the Aadhaar name ${last.aadhaarName} did not match ${last.applicantName}` : `the Aadhaar name of ${last?.applicantName} was not received`} (${when(last?.at)})`
                    : last?.status === "PENDING"
                    ? `${last.applicantName} started the Aadhaar eSign ${when(last.at)}`
                    : "Any one applicant signs with Aadhaar on the Customer Portal"}
                </span>
              </div>
              {s.attempts?.length > 1 && (
                <div style={{ fontSize: "1.3rem", color: "#475467" }}>
                  <b style={{ color: "#344054" }}>Every eSign attempt</b>
                  {s.attempts.map((a) => (
                    <div key={a.no}>
                      {a.no}. {a.applicantName} · {a.status.replace(/_/g, " ").toLowerCase()}
                      {a.aadhaarName ? ` · Aadhaar name ${a.aadhaarName}` : ""} · {when(a.completedAt || a.startedAt)}
                    </div>
                  ))}
                </div>
              )}
              <p style={{ margin: 0, fontSize: "1.3rem", color: "#667085" }}>{s.lockLine}</p>
            </>
          )}
        </div>
        <div className={settingsCss.modalFooter} style={{ justifyContent: "flex-end", gap: "1rem" }}>
          <Button btnName="Close" style={btnSec} clicked={onClose} />
          <Button btnName="Download" style={{ ...btnPri, opacity: s?.letter?.state === "MADE" || signed ? 1 : 0.5 }} disable={!(s?.letter?.state === "MADE" || signed)} clicked={download} />
        </div>
      </Box>
    </Modal>
  );
}
